import { 
  CreditCard, 
  Globe, 
  Shield, 
  BarChart3, 
  Zap, 
  Users, 
  Smartphone,
  Building,
  Banknote,
  Lock,
  TrendingUp,
  Settings,
  Code,
  Star,
  Brain
} from 'lucide-react';

const mockData = {
  languages: {
    en: {
      heroStats: [
        {
          value: "150+",
          label: "Countries Supported"
        },
        {
          value: "99.9%",
          label: "Uptime Guarantee"
        },
        {
          value: "<100ms",
          label: "API Response Time"
        }
      ],
      nav: {
        features: "Features",
        services: "Services",
        pricing: "Pricing",
        integration: "Integration",
        support: "Support",
        signIn: "Sign In",
        getStarted: "Get Started"
      },
      hero: {
        badge: "White Label Payment Gateway SaaS",
        title: "Launch your own",
        titleHighlight: "Payment Platform",
        subtitle: "Complete white-label payment gateway solution for fintech startups, payment facilitators, and banks. Process payments globally with advanced fraud protection, intelligent routing, and full customization.",
        startTrial: "Start Free Trial",
        watchDemo: "Explore Demo (free)",
        multiPaymentLine1: "Multi Currencies, Multi Countries, Multi Processors",
        multiPaymentLine2: "includes Credit and Debit Cards payments, Google/Apple Pay, Crypto, ACH, Banks transfers and many local Wallets QR codes"
      },
      sections: {
        featuresTitle: "Everything You Need to Launch",
        featuresSubtitle: "Comprehensive payment infrastructure with enterprise-grade security and performance",
        clientsTitle: "Powering Modern Payment Businesses",
        clientsSubtitle: "Join innovative banks, payment facilitators, and fintechs across Latin America",
        servicesTitle: "Standalone Services",
        servicesSubtitle: "End-to-end payment solutions designed for modern businesses",
        latamProcessorsTitle: "PaymentPower has processor coverage in more than 6 countries",
        latamProcessorsSubtitle: "Strategic partnerships across Latin America",
        targetAudienceTitle: "Built for Payment Industry Leaders",
        targetAudienceSubtitle: "Trusted by banks, payment facilitators, fintechs, and enterprises across Latin America and beyond",
        footerDescription: "The most comprehensive white-label payment gateway solution for modern businesses.",
        footerCopyright: "© 2025 PaymentPower. All rights reserved.",
        pricingTitle: "Transparent Pricing",
        pricingSubtitle: "Choose the plan that fits your business needs. All plans include white-label customization.",
        faqTitle: "Frequently Asked Questions",
        faqSubtitle: "Everything you need to know about our white-label payment gateway",
        ctaTitle: "Ready to Launch Your Payment Platform?",
        ctaSubtitle: "Chosen by fintech builders, PayFacs, and banks scaling modern payment solutions.",
        ctaButton1: "Start Free Trial",
        ctaButton2: "Contact Sales"
      }
    },
    es: {
      heroStats: [
        {
          value: "150+",
          label: "Países Soportados"
        },
        {
          value: "99.9%",
          label: "Garantía de Tiempo Activo"
        },
        {
          value: "<100ms",
          label: "Tiempo de Respuesta API"
        }
      ],
      nav: {
        features: "Características",
        services: "Servicios",
        pricing: "Precios",
        integration: "Integración",
        support: "Soporte",
        signIn: "Iniciar Sesión",
        getStarted: "Comenzar"
      },
      hero: {
        badge: "SaaS de Pasarela de Pago de Marca Blanca",
        title: "Lanza tu propia",
        titleHighlight: "Plataforma de Pagos",
        subtitle: "Solución completa de pasarela de pagos de marca blanca para startups fintech, facilitadores de pago y bancos. Procesa pagos globalmente con protección avanzada contra fraudes, enrutamiento inteligente y personalización completa.",
        startTrial: "Comenzar Prueba Gratis",
        watchDemo: "Explorar Demo (gratis)",
        multiPaymentLine1: "Multi Monedas, Multi Países, Multi Procesadores",
        multiPaymentLine2: "incluye pagos con Tarjetas de Crédito y Débito, Google/Apple Pay, Cripto, ACH, Transferencias Bancarias y códigos QR de muchas Billeteras locales"
      },
      sections: {
        featuresTitle: "Todo lo que Necesitas para Lanzar",
        featuresSubtitle: "Infraestructura de pagos integral con seguridad y rendimiento de nivel empresarial",
        clientsTitle: "Impulsando Negocios de Pagos Modernos",
        clientsSubtitle: "Únete a bancos innovadores, facilitadores de pago y fintechs en América Latina",
        servicesTitle: "Servicios Independientes",
        servicesSubtitle: "Soluciones de pago completas diseñadas para empresas modernas",
        latamProcessorsTitle: "PaymentPower tiene cobertura de procesadores en más de 6 países",
        latamProcessorsSubtitle: "Alianzas estratégicas a través de América Latina",
        targetAudienceTitle: "Construido para Líderes de la Industria de Pagos",
        targetAudienceSubtitle: "Confiado por bancos, facilitadores de pago, fintechs y empresas en América Latina y más allá",
        footerDescription: "La solución de pasarela de pagos de marca blanca más completa para empresas modernas.",
        footerCopyright: "© 2025 PaymentPower. Todos los derechos reservados.",
        pricingTitle: "Precios Transparentes",
        pricingSubtitle: "Elige el plan que se adapte a las necesidades de tu negocio. Todos los planes incluyen personalización de marca blanca.",
        faqTitle: "Preguntas Frecuentes",
        faqSubtitle: "Todo lo que necesitas saber sobre nuestra pasarela de pago de marca blanca",
        ctaTitle: "¿Listo para Lanzar tu Plataforma de Pagos?",
        ctaSubtitle: "Elegido por constructores fintech, PayFacs y bancos escalando soluciones de pago modernas.",
        ctaButton1: "Comenzar Prueba Gratis",
        ctaButton2: "Contactar Ventas"
      }
    }
  },

  mainFeatures: {
    en: [
      {
        icon: Building,
        title: "Full White-Label Branding",
        description: "Complete customization with your brand, logos, colors, and domain",
        details: "Transform our platform into your own branded payment solution. Custom domains, complete UI customization, branded documentation, and merchant portals that reflect your brand identity.",
        technologies: ["Custom CSS", "Domain Mapping", "Brand Assets", "UI Themes"]
      },
      {
        icon: Globe,
        title: "Global Payment Processing",
        description: "Accept payments in 150+ currencies with local payment methods",
        details: "Process payments worldwide with support for 150+ currencies, local payment methods, and regional compliance. Smart currency conversion and settlement in your preferred currency.",
        technologies: ["Multi-Currency", "Local APMs", "FX Conversion", "Regional Compliance"]
      },
      {
        icon: TrendingUp,
        title: "Intelligent Payment Routing",
        description: "AI-powered routing optimization for maximum approval rates",
        details: "Our smart routing engine automatically selects the best payment path based on success rates, costs, and performance metrics. A/B testing and machine learning optimization included.",
        technologies: ["AI Routing", "A/B Testing", "Performance Analytics", "Cost Optimization"]
      },
      {
        icon: Shield,
        title: "Advanced Fraud Protection",
        description: "Real-time fraud detection with machine learning algorithms",
        details: "Enterprise-grade fraud prevention using AI and machine learning. Real-time risk scoring, customizable rules engine, comprehensive chargeback management, and 3D Secure 2.0 authentication for maximum security.",
        technologies: ["ML Fraud Detection", "Risk Scoring", "Rules Engine", "3D Secure 2.0", "Tokenization"]
      },
      {
        icon: Brain,
        title: "AI Insights",
        description: "Intelligent business intelligence powered by artificial intelligence",
        details: "Leverage AI to gain actionable insights from your payment data. Predictive analytics, customer behavior patterns, revenue forecasting, and automated recommendations to optimize your payment operations and increase conversions.",
        technologies: ["Predictive Analytics", "Pattern Recognition", "Revenue Forecasting", "Smart Recommendations"]
      }
    ],
    es: [
      {
        icon: Building,
        title: "Marca Blanca Completa",
        description: "Personalización completa con tu marca, logos, colores y dominio",
        details: "Transforma nuestra plataforma en tu propia solución de pagos con marca. Dominios personalizados, personalización completa de UI, documentación con marca y portales de comerciantes que reflejan tu identidad de marca.",
        technologies: ["CSS Personalizado", "Mapeo de Dominio", "Activos de Marca", "Temas UI"]
      },
      {
        icon: Globe,
        title: "Procesamiento Global de Pagos",
        description: "Acepta pagos en más de 150 monedas con métodos de pago locales",
        details: "Procesa pagos en todo el mundo con soporte para más de 150 monedas, métodos de pago locales y cumplimiento regional. Conversión inteligente de monedas y liquidación en tu moneda preferida.",
        technologies: ["Multi-Moneda", "APMs Locales", "Conversión FX", "Cumplimiento Regional"]
      },
      {
        icon: TrendingUp,
        title: "Enrutamiento Inteligente de Pagos",
        description: "Optimización de enrutamiento impulsada por IA para máximas tasas de aprobación",
        details: "Nuestro motor de enrutamiento inteligente selecciona automáticamente la mejor ruta de pago basada en tasas de éxito, costos y métricas de rendimiento. Incluye pruebas A/B y optimización de aprendizaje automático.",
        technologies: ["Enrutamiento IA", "Pruebas A/B", "Análisis de Rendimiento", "Optimización de Costos"]
      },
      {
        icon: Shield,
        title: "Protección Avanzada contra Fraudes",
        description: "Detección de fraudes en tiempo real con algoritmos de aprendizaje automático",
        details: "Protección contra fraudes de nivel empresarial usando IA y aprendizaje automático. Puntuación de riesgo en tiempo real, motor de reglas personalizable, gestión integral de contracargos y autenticación 3D Secure 2.0 para máxima seguridad.",
        technologies: ["Detección ML de Fraudes", "Puntuación de Riesgo", "Motor de Reglas", "3D Secure 2.0", "Tokenización"]
      },
      {
        icon: Brain,
        title: "Insights de IA",
        description: "Inteligencia de negocios impulsada por inteligencia artificial",
        details: "Aprovecha la IA para obtener información procesable de tus datos de pago. Análisis predictivo, patrones de comportamiento del cliente, pronósticos de ingresos y recomendaciones automáticas para optimizar tus operaciones de pago y aumentar conversiones.",
        technologies: ["Análisis Predictivo", "Reconocimiento de Patrones", "Pronóstico de Ingresos", "Recomendaciones Inteligentes"]
      }
    ]
  },

  additionalFeatures: {
    en: [
      {
        icon: CreditCard,
        title: "Payment Channels",
        description: "Checkout button, Payment links, Plugins, API, recurring charges, etc.",
        tags: ["Checkout", "Payment Links", "API", "Recurring", "QR Codes"]
      },
      {
        icon: BarChart3,
        title: "Real-Time Analytics",
        description: "Comprehensive dashboards with transaction and financial reporting",
        tags: ["Dashboards", "Reporting", "Business Intelligence"]
      },
      {
        icon: Banknote,
        title: "Settlement Reports and Disbursement",
        description: "Automated settlement reports and disbursement to the merchant's bank account",
        tags: ["Settlement", "Disbursement", "Bank Transfer", "Reconciliation"]
      },
      {
        icon: Zap,
        title: "Instant Merchant Onboarding",
        description: "Automated KYB/KYC processes for rapid merchant activation",
        tags: ["KYB/KYC", "PayFac", "Instant Approval"]
      },
      {
        icon: Code,
        title: "Developer-First APIs",
        description: "RESTful APIs, SDKs, and webhooks for seamless integration",
        tags: ["REST APIs", "SDKs", "Webhooks", "Documentation"]
      },
      {
        icon: Lock,
        title: "PCI DSS Compliance",
        description: "Level 1 PCI DSS certification with end-to-end encryption",
        tags: ["PCI Level 1", "Encryption", "Tokenization"]
      },
      {
        icon: Shield,
        title: "3D Secure Authentication",
        description: "Advanced card authentication protocol for secure online transactions",
        tags: ["3DS 2.0", "Authentication", "Liability Shift", "Fraud Prevention"]
      },
      {
        icon: Code,
        title: "Private Tokenization",
        description: "Replace sensitive card data with secure tokens for recurring payments",
        tags: ["Card Tokenization", "Network Tokens", "Secure Storage", "PCI Scope Reduction"]
      },
      {
        icon: Users,
        title: "Multi-Tenant Architecture",
        description: "Scalable platform supporting unlimited merchants and transactions",
        tags: ["Multi-Tenant", "Scalable", "High Performance"]
      }
    ],
    es: [
      {
        icon: CreditCard,
        title: "Canales de Pago",
        description: "Botón de pago, Enlaces de pago, Plugins, API, cobros recurrentes, etc.",
        tags: ["Checkout", "Enlaces de Pago", "API", "Recurrentes", "Códigos QR"]
      },
      {
        icon: BarChart3,
        title: "Análisis en Tiempo Real",
        description: "Dashboards integrales con informes de transacciones y financieros",
        tags: ["Dashboards", "Reportes", "Inteligencia de Negocios"]
      },
      {
        icon: Banknote,
        title: "Reportes de Liquidación y Desembolso",
        description: "Reportes automáticos de liquidación y desembolso a la cuenta bancaria del comerciante",
        tags: ["Liquidación", "Desembolso", "Transferencia Bancaria", "Conciliación"]
      },
      {
        icon: Zap,
        title: "Incorporación Instantánea de Comerciantes",
        description: "Procesos automatizados KYB/KYC para activación rápida de comerciantes",
        tags: ["KYB/KYC", "PayFac", "Aprobación Instantánea"]
      },
      {
        icon: Code,
        title: "APIs Centradas en Desarrolladores",
        description: "APIs RESTful, SDKs y webhooks para integración perfecta",
        tags: ["APIs REST", "SDKs", "Webhooks", "Documentación"]
      },
      {
        icon: Lock,
        title: "Cumplimiento PCI DSS",
        description: "Certificación PCI DSS Nivel 1 con cifrado de extremo a extremo",
        tags: ["PCI Nivel 1", "Cifrado", "Tokenización"]
      },
      {
        icon: Shield,
        title: "Autenticación 3D Secure",
        description: "Protocolo avanzado de autenticación de tarjetas para transacciones online seguras",
        tags: ["3DS 2.0", "Autenticación", "Transferencia de Responsabilidad", "Prevención de Fraudes"]
      },
      {
        icon: Code,
        title: "Tokenización Privada",
        description: "Reemplaza datos sensibles de tarjetas con tokens seguros para pagos recurrentes",
        tags: ["Tokenización de Tarjetas", "Tokens de Red", "Almacenamiento Seguro", "Reducción PCI"]
      },
      {
        icon: Users,
        title: "Arquitectura Multi-Inquilino",
        description: "Plataforma escalable que soporta comerciantes y transacciones ilimitadas",
        tags: ["Multi-Inquilino", "Escalable", "Alto Rendimiento"]
      }
    ]
  },

  pricingPlans: {
    en: [
      {
        name: "Starter",
        price: "$999",
        description: "Perfect for fintech startups",
        features: [
          "Up to 10,000 transactions/month",
          "Basic white-label branding",
          "1 processor integration",
          "Standard API access",
          "Email support",
          "Basic fraud protection"
        ],
        cta: "Start Free Trial",
        popular: false
      },
      {
        name: "Professional",
        price: "$1,499",
        description: "Ideal for growing payment facilitators",
        features: [
          "Up to 50,000 transactions/month",
          "Full white-label customization",
          "Advanced API features",
          "Priority support",
          "Advanced fraud protection",
          "Real-time analytics",
          "3D Secure 2.0 authentication",
          "Private tokenization"
        ],
        cta: "Get Started",
        popular: true
      },
      {
        name: "Enterprise",
        price: "Starting from $2,500",
        description: "For banks and large enterprises",
        features: [
          "Up to 300,000 transactions/month",
          "Complete platform customization",
          "Dedicated infrastructure",
          "24/7 dedicated support",
          "Enterprise fraud suite",
          "3D Secure 2.0 & Tokenization",
          "Advanced reporting",
          "SLA guarantees",
          "Custom features"
        ],
        cta: "Contact Sales",
        popular: false
      }
    ],
    es: [
      {
        name: "Inicial",
        price: "$999",
        description: "Perfecto para startups fintech",
        features: [
          "Hasta 10,000 transacciones/mes",
          "Marca blanca básica",
          "1 integración de procesador",
          "Acceso estándar a API",
          "Soporte por email",
          "Protección básica contra fraudes"
        ],
        cta: "Comenzar Prueba Gratis",
        popular: false
      },
      {
        name: "Profesional",
        price: "$1,499",
        description: "Ideal para facilitadores de pago en crecimiento",
        features: [
          "Hasta 50,000 transacciones/mes",
          "Personalización completa de marca blanca",
          "Características avanzadas de API",
          "Soporte prioritario",
          "Protección avanzada contra fraudes",
          "Análisis en tiempo real",
          "Autenticación 3D Secure 2.0",
          "Tokenización privada"
        ],
        cta: "Comenzar",
        popular: true
      },
      {
        name: "Empresas",
        price: "Desde $2,500",
        description: "Para bancos y grandes empresas",
        features: [
          "Hasta 300,000 transacciones/mes",
          "Personalización completa de plataforma",
          "Infraestructura dedicada",
          "Soporte dedicado 24/7",
          "Suite empresarial contra fraudes",
          "3D Secure 2.0 y Tokenización",
          "Reportes avanzados",
          "Garantías SLA",
          "Características personalizadas"
        ],
        cta: "Contactar Ventas",
        popular: false
      }
    ]
  },

  clients: {
    en: [
      {
        name: "GiveMe",
        country: "USA",
        logo: "https://customer-assets.emergentagent.com/job_3de5042f-38bc-4c59-be9d-105ed5dfd1b8/artifacts/eo7bjk15_giveme.webp",
        url: "https://www.mygiveme.com/web/",
        description: "Conversational commerce suite and promotion platform"
      },
      {
        name: "MIO",
        country: "Dominican Republic",
        logo: "https://cdn.prod.website-files.com/604a367df2727a52b715143f/604a367df2727a548315145e_img-logo.svg",
        url: "https://www.mio.com.do/",
        description: "Digital account and card by Banreservas to pay, collect and transfer money"
      },
      {
        name: "Banorte",
        country: "Mexico",
        logo: "/banorte-logo.png",
        url: "https://www.banorte.com/",
        description: "Leading Mexican banking and financial services group"
      },
      {
        name: "Afinia Payments",
        country: "Latin America",
        logo: "https://customer-assets.emergentagent.com/job_payment-gateway-93/artifacts/exuh0faa_afinia.svg",
        url: "https://www.afiniapayments.com/",
        description: "Regional payment processing platform"
      },
      {
        name: "TutumPay",
        country: "Mexico",
        logo: "https://customer-assets.emergentagent.com/job_payment-gateway-93/artifacts/cnvtqmbe_tutum%20pay.png",
        url: "https://www.tutumpay.mx/",
        description: "Digital payment solutions for Mexican market"
      },
      {
        name: "PayMóvil",
        country: "Colombia",
        logo: "https://customer-assets.emergentagent.com/job_payment-gateway-93/artifacts/vziha892_paym%C3%B3vil.svg",
        url: "https://www.paymovil.co/pasareladepagos",
        description: "Colombian payment gateway specialist"
      },
      {
        name: "Atix",
        country: "Peru",
        logo: "https://customer-assets.emergentagent.com/job_payment-gateway-93/artifacts/d7hltqqw_atix.svg",
        url: "https://www.atix.com.pe/",
        description: "Peruvian digital payment solutions provider"
      },
      {
        name: "Global Bridge Connections",
        country: "Paraguay",
        logo: "https://customer-assets.emergentagent.com/job_payment-gateway-93/artifacts/4j58s55q_global%20bride%20connections.svg",
        url: "https://www.globalbridgeconnections.com.py/",
        description: "Cross-border payment infrastructure"
      },
      {
        name: "B89",
        country: "Peru",
        logo: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='41' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M17.5042 12.8843C17.0919 13.3168 17.1353 13.9873 17.6127 14.3766C17.9382 14.6362 18.242 14.939 18.5458 15.2634C19.0449 15.8258 19.4789 16.5179 19.8912 17.383C20.3035 18.2266 20.4988 19.2647 20.4988 20.4976C20.4988 21.5574 20.2818 22.5739 19.8478 23.5256C19.4138 24.4772 18.8062 25.3208 18.025 26.0345C17.2438 26.7699 16.3324 27.3322 15.2691 27.7864C14.2275 28.219 13.0774 28.4353 11.8188 28.4353H1.77164C1.22914 28.4353 0.773438 27.9811 0.773438 27.4187V1.68061C0.773438 1.11826 1.22914 0.664062 1.77164 0.664062H11.6235C12.6868 0.664062 13.7067 0.85872 14.6615 1.26966C15.6163 1.68061 16.4409 2.24296 17.157 2.93507C17.8731 3.64882 18.4156 4.44908 18.8279 5.37911C19.2402 6.30915 19.4355 7.30407 19.4355 8.36387C19.4355 9.22902 19.2836 10.0077 18.9798 10.6781C18.676 11.3486 18.3505 11.8893 18.025 12.3219C17.8297 12.5166 17.6778 12.7112 17.5042 12.8843ZM4.87475 11.1107C4.87475 11.6731 5.33045 12.1273 5.87295 12.1273H11.6018C12.7085 12.1273 13.6199 11.7596 14.3794 11.0026C15.1172 10.2672 15.5078 9.38042 15.5078 8.3855C15.5078 7.39058 15.1389 6.50381 14.3794 5.76843C13.6199 5.03306 12.7085 4.64374 11.6235 4.64374H5.87295C5.33045 4.64374 4.87475 5.09794 4.87475 5.66029V11.1107ZM11.8188 24.4772C12.4481 24.4772 13.0557 24.3691 13.6199 24.1312C14.1841 23.8933 14.6615 23.5688 15.0738 23.1795C15.4861 22.7902 15.7899 22.3144 16.0286 21.7953C16.2673 21.2546 16.3758 20.6922 16.3758 20.065C16.3758 19.4378 16.2673 18.8754 16.0286 18.3347C15.7899 17.794 15.4861 17.3398 15.0738 16.9505C14.6615 16.5611 14.1841 16.2367 13.6199 15.9988C13.0557 15.7609 12.4481 15.6527 11.8188 15.6527H5.87295C5.33045 15.6527 4.87475 16.1069 4.87475 16.6693V23.4607C4.87475 24.023 5.33045 24.4772 5.87295 24.4772H11.8188Z' fill='%23050201'%3E%3C/path%3E%3Cpath d='M22.0547 20.9346C22.0547 21.0857 22.0547 21.2584 22.076 21.4095C22.1185 21.906 22.3311 22.3593 22.6926 22.7047C23.0541 23.0501 23.5219 23.2228 24.011 23.2228C24.2662 23.2228 24.5426 23.1581 24.7766 23.0717C25.0105 22.9638 25.2444 22.8127 25.4145 22.6184C25.6059 22.4241 25.7335 22.2082 25.8185 21.9492C25.9036 21.6901 25.9461 21.4311 25.9461 21.172V20.913C25.9461 18.085 28.3277 15.7752 31.2835 15.6673C31.3686 15.6673 31.4324 15.6888 31.5174 15.6888H31.7301C34.7071 15.7968 37.0462 18.1066 37.0462 20.9346V21.1936C37.0249 21.4527 37.0675 21.7333 37.1738 21.9708C37.2588 22.2298 37.4077 22.4457 37.5991 22.64C37.7905 22.8342 38.0031 22.9854 38.237 23.0933C38.4709 23.2012 38.7474 23.2444 39.0025 23.2444H39.0451C39.5342 23.2444 40.002 23.0717 40.3635 22.7263C40.725 22.3809 40.9376 21.9276 40.9801 21.4311C40.9801 21.28 41.0014 21.1073 41.0014 20.9561C41.0014 19.812 40.7675 18.6895 40.3209 17.6317C39.8744 16.5739 39.2365 15.6241 38.4284 14.8469C38.0882 14.5231 37.8968 14.0698 37.8543 13.5949C37.8117 13.1199 37.9393 12.645 38.2158 12.278C39.0876 11.0907 39.5554 9.64439 39.5554 8.15486C39.5554 4.0101 35.9404 0.664062 31.4749 0.664062C27.0093 0.664062 23.3944 4.0101 23.3944 8.15486C23.3944 9.64439 23.8622 11.0692 24.734 12.278C25.0105 12.6666 25.138 13.1199 25.0955 13.5949C25.053 14.0698 24.8616 14.5015 24.5214 14.8469C23.7346 15.6457 23.0967 16.5955 22.6501 17.6317C22.2673 18.6679 22.0547 19.7904 22.0547 20.9346ZM31.5174 4.11803C33.8778 4.11803 35.7916 5.8882 35.7916 8.0901C35.7916 10.2704 33.8778 12.0622 31.5174 12.0622C29.157 12.0622 27.2432 10.292 27.2432 8.0901C27.2432 5.90978 29.157 4.11803 31.5174 4.11803Z' fill='%23050201'%3E%3C/path%3E%3C/svg%3E",
        url: "https://www.b89.io/",
        description: "International money transfer and fintech services"
      }
    ],
    es: [
      {
        name: "GiveMe",
        country: "EE.UU.",
        logo: "https://customer-assets.emergentagent.com/job_3de5042f-38bc-4c59-be9d-105ed5dfd1b8/artifacts/eo7bjk15_giveme.webp",
        url: "https://www.mygiveme.com/web/",
        description: "Suite de comercio conversacional y plataforma de promociones"
      },
      {
        name: "MIO",
        country: "República Dominicana",
        logo: "https://cdn.prod.website-files.com/604a367df2727a52b715143f/604a367df2727a548315145e_img-logo.svg",
        url: "https://www.mio.com.do/",
        description: "Cuenta digital y tarjeta de Banreservas para pagar, cobrar y transferir dinero"
      },
      {
        name: "Banorte",
        country: "México",
        logo: "/banorte-logo.png",
        url: "https://www.banorte.com/",
        description: "Grupo financiero y bancario líder en México"
      },
      {
        name: "Afinia Payments",
        country: "América Latina",
        logo: "https://customer-assets.emergentagent.com/job_payment-gateway-93/artifacts/exuh0faa_afinia.svg",
        url: "https://www.afiniapayments.com/",
        description: "Plataforma regional de procesamiento de pagos"
      },
      {
        name: "TutumPay",
        country: "México",
        logo: "https://customer-assets.emergentagent.com/job_payment-gateway-93/artifacts/cnvtqmbe_tutum%20pay.png",
        url: "https://www.tutumpay.mx/",
        description: "Soluciones de pago digital para el mercado mexicano"
      },
      {
        name: "PayMóvil",
        country: "Colombia",
        logo: "https://customer-assets.emergentagent.com/job_payment-gateway-93/artifacts/vziha892_paym%C3%B3vil.svg",
        url: "https://www.paymovil.co/pasareladepagos",
        description: "Especialista en pasarela de pagos colombiana"
      },
      {
        name: "Atix",
        country: "Perú",
        logo: "https://customer-assets.emergentagent.com/job_payment-gateway-93/artifacts/d7hltqqw_atix.svg",
        url: "https://www.atix.com.pe/",
        description: "Proveedor de soluciones de pago digital peruano"
      },
      {
        name: "Global Bridge Connections",
        country: "Paraguay",
        logo: "https://customer-assets.emergentagent.com/job_payment-gateway-93/artifacts/4j58s55q_global%20bride%20connections.svg",
        url: "https://www.globalbridgeconnections.com.py/",
        description: "Infraestructura de pagos transfronterizos"
      },
      {
        name: "B89",
        country: "Perú",
        logo: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='41' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M17.5042 12.8843C17.0919 13.3168 17.1353 13.9873 17.6127 14.3766C17.9382 14.6362 18.242 14.939 18.5458 15.2634C19.0449 15.8258 19.4789 16.5179 19.8912 17.383C20.3035 18.2266 20.4988 19.2647 20.4988 20.4976C20.4988 21.5574 20.2818 22.5739 19.8478 23.5256C19.4138 24.4772 18.8062 25.3208 18.025 26.0345C17.2438 26.7699 16.3324 27.3322 15.2691 27.7864C14.2275 28.219 13.0774 28.4353 11.8188 28.4353H1.77164C1.22914 28.4353 0.773438 27.9811 0.773438 27.4187V1.68061C0.773438 1.11826 1.22914 0.664062 1.77164 0.664062H11.6235C12.6868 0.664062 13.7067 0.85872 14.6615 1.26966C15.6163 1.68061 16.4409 2.24296 17.157 2.93507C17.8731 3.64882 18.4156 4.44908 18.8279 5.37911C19.2402 6.30915 19.4355 7.30407 19.4355 8.36387C19.4355 9.22902 19.2836 10.0077 18.9798 10.6781C18.676 11.3486 18.3505 11.8893 18.025 12.3219C17.8297 12.5166 17.6778 12.7112 17.5042 12.8843ZM4.87475 11.1107C4.87475 11.6731 5.33045 12.1273 5.87295 12.1273H11.6018C12.7085 12.1273 13.6199 11.7596 14.3794 11.0026C15.1172 10.2672 15.5078 9.38042 15.5078 8.3855C15.5078 7.39058 15.1389 6.50381 14.3794 5.76843C13.6199 5.03306 12.7085 4.64374 11.6235 4.64374H5.87295C5.33045 4.64374 4.87475 5.09794 4.87475 5.66029V11.1107ZM11.8188 24.4772C12.4481 24.4772 13.0557 24.3691 13.6199 24.1312C14.1841 23.8933 14.6615 23.5688 15.0738 23.1795C15.4861 22.7902 15.7899 22.3144 16.0286 21.7953C16.2673 21.2546 16.3758 20.6922 16.3758 20.065C16.3758 19.4378 16.2673 18.8754 16.0286 18.3347C15.7899 17.794 15.4861 17.3398 15.0738 16.9505C14.6615 16.5611 14.1841 16.2367 13.6199 15.9988C13.0557 15.7609 12.4481 15.6527 11.8188 15.6527H5.87295C5.33045 15.6527 4.87475 16.1069 4.87475 16.6693V23.4607C4.87475 24.023 5.33045 24.4772 5.87295 24.4772H11.8188Z' fill='%23050201'%3E%3C/path%3E%3Cpath d='M22.0547 20.9346C22.0547 21.0857 22.0547 21.2584 22.076 21.4095C22.1185 21.906 22.3311 22.3593 22.6926 22.7047C23.0541 23.0501 23.5219 23.2228 24.011 23.2228C24.2662 23.2228 24.5426 23.1581 24.7766 23.0717C25.0105 22.9638 25.2444 22.8127 25.4145 22.6184C25.6059 22.4241 25.7335 22.2082 25.8185 21.9492C25.9036 21.6901 25.9461 21.4311 25.9461 21.172V20.913C25.9461 18.085 28.3277 15.7752 31.2835 15.6673C31.3686 15.6673 31.4324 15.6888 31.5174 15.6888H31.7301C34.7071 15.7968 37.0462 18.1066 37.0462 20.9346V21.1936C37.0249 21.4527 37.0675 21.7333 37.1738 21.9708C37.2588 22.2298 37.4077 22.4457 37.5991 22.64C37.7905 22.8342 38.0031 22.9854 38.237 23.0933C38.4709 23.2012 38.7474 23.2444 39.0025 23.2444H39.0451C39.5342 23.2444 40.002 23.0717 40.3635 22.7263C40.725 22.3809 40.9376 21.9276 40.9801 21.4311C40.9801 21.28 41.0014 21.1073 41.0014 20.9561C41.0014 19.812 40.7675 18.6895 40.3209 17.6317C39.8744 16.5739 39.2365 15.6241 38.4284 14.8469C38.0882 14.5231 37.8968 14.0698 37.8543 13.5949C37.8117 13.1199 37.9393 12.645 38.2158 12.278C39.0876 11.0907 39.5554 9.64439 39.5554 8.15486C39.5554 4.0101 35.9404 0.664062 31.4749 0.664062C27.0093 0.664062 23.3944 4.0101 23.3944 8.15486C23.3944 9.64439 23.8622 11.0692 24.734 12.278C25.0105 12.6666 25.138 13.1199 25.0955 13.5949C25.053 14.0698 24.8616 14.5015 24.5214 14.8469C23.7346 15.6457 23.0967 16.5955 22.6501 17.6317C22.2673 18.6679 22.0547 19.7904 22.0547 20.9346ZM31.5174 4.11803C33.8778 4.11803 35.7916 5.8882 35.7916 8.0901C35.7916 10.2704 33.8778 12.0622 31.5174 12.0622C29.157 12.0622 27.2432 10.292 27.2432 8.0901C27.2432 5.90978 29.157 4.11803 31.5174 4.11803Z' fill='%23050201'%3E%3C/path%3E%3C/svg%3E",
        url: "https://www.b89.io/",
        description: "Servicios fintech y transferencias internacionales de dinero"
      }
    ]
  },

  services: {
    en: [
      {
        icon: CreditCard,
        title: "Payment Processing",
        description: "Comprehensive payment processing solutions",
        features: ["Card Processing", "Bank Transfers", "Digital Wallets", "Alternative Payments"]
      },
      {
        icon: Shield,
        title: "Anti Fraud",
        description: "Advanced fraud detection and prevention system",
        features: ["AI-powered detection", "Real-time monitoring", "Risk scoring", "Chargeback protection"]
      },
      {
        icon: Settings,
        title: "3D Secure",
        description: "Advanced authentication for online card payments",
        features: ["3DS 2.0 protocol", "Frictionless flow", "Liability shift", "Higher approval rates"]
      },
      {
        icon: Lock,
        title: "Tokenization",
        description: "Secure card data protection through tokenization",
        features: ["Network tokens", "PCI scope reduction", "Enhanced security", "Seamless integration"]
      },
      {
        icon: Smartphone,
        title: "Soft POS",
        description: "Transform any mobile device into a payment terminal",
        features: ["NFC payments", "QR code support", "Contactless transactions", "Mobile receipts"]
      },
      {
        icon: Banknote,
        title: "Crypto Payments",
        description: "Accept cryptocurrency payments with USDT/USDC stablecoins",
        features: ["USDT support", "USDC support", "Instant settlement", "Low transaction fees"]
      },
      {
        icon: Zap,
        title: "Visa Direct",
        description: "Real-time money movement through Visa's network",
        features: ["Instant transfers", "Global reach", "24/7 processing", "Account-to-account"]
      },
      {
        icon: Code,
        title: "Processor Integrations",
        description: "Seamless integration with leading payment processors",
        features: ["Multi-processor support", "Unified API", "Failover routing", "Regional optimization"]
      }
    ],
    es: [
      {
        icon: CreditCard,
        title: "Procesamiento de Pagos",
        description: "Soluciones integrales de procesamiento de pagos",
        features: ["Procesamiento de Tarjetas", "Transferencias Bancarias", "Billeteras Digitales", "Pagos Alternativos"]
      },
      {
        icon: Shield,
        title: "Anti Fraude",
        description: "Sistema avanzado de detección y prevención de fraudes",
        features: ["Detección con IA", "Monitoreo en tiempo real", "Puntuación de riesgo", "Protección contracargos"]
      },
      {
        icon: Settings,
        title: "3D Secure",
        description: "Autenticación avanzada para pagos online con tarjeta",
        features: ["Protocolo 3DS 2.0", "Flujo sin fricción", "Transferencia responsabilidad", "Mayores tasas aprobación"]
      },
      {
        icon: Lock,
        title: "Tokenización",
        description: "Protección segura de datos de tarjetas mediante tokenización",
        features: ["Tokens de red", "Reducción alcance PCI", "Seguridad mejorada", "Integración fluida"]
      },
      {
        icon: Smartphone,
        title: "Soft POS",
        description: "Convierte cualquier dispositivo móvil en terminal de pago",
        features: ["Pagos NFC", "Soporte QR", "Transacciones contactless", "Recibos móviles"]
      },
      {
        icon: Banknote,
        title: "Pagos Cripto",
        description: "Acepta pagos con criptomonedas usando stablecoins USDT/USDC",
        features: ["Soporte USDT", "Soporte USDC", "Liquidación instantánea", "Tarifas bajas"]
      },
      {
        icon: Zap,
        title: "Visa Direct",
        description: "Movimiento de dinero en tiempo real a través de la red Visa",
        features: ["Transferencias instantáneas", "Alcance global", "Procesamiento 24/7", "Cuenta a cuenta"]
      },
      {
        icon: Code,
        title: "Integraciones Procesadores",
        description: "Integración fluida con procesadores de pago líderes",
        features: ["Soporte multi-procesador", "API unificada", "Enrutamiento failover", "Optimización regional"]
      }
    ]
  },

  latamProcessors: {
    en: [
      {
        country: "Mexico",
        processors: ["Kushki", "Openpay", "Banorte", "Afirme"],
        flag: "🇲🇽",
        flagCode: "mx"
      },
      {
        country: "Dominican Republic",
        processors: ["Azul"],
        flag: "🇩🇴",
        flagCode: "do"
      },
      {
        country: "Costa Rica", 
        processors: ["Lafise"],
        flag: "🇨🇷",
        flagCode: "cr"
      },
      {
        country: "Panama",
        processors: ["Metrobank", "Credicorp", "BCP"], 
        flag: "🇵🇦",
        flagCode: "pa"
      },
      {
        country: "Colombia",
        processors: ["Redeban"],
        flag: "🇨🇴",
        flagCode: "co"
      },
      {
        country: "Peru",
        processors: ["Niubiz", "Izipay"],
        flag: "🇵🇪",
        flagCode: "pe"
      }
    ],
    es: [
      {
        country: "México",
        processors: ["Kushki", "Openpay", "Banorte", "Afirme"],
        flag: "🇲🇽",
        flagCode: "mx"
      },
      {
        country: "República Dominicana",
        processors: ["Azul"],
        flag: "🇩🇴",
        flagCode: "do"
      },
      {
        country: "Costa Rica",
        processors: ["Lafise"], 
        flag: "🇨🇷",
        flagCode: "cr"
      },
      {
        country: "Panamá",
        processors: ["Metrobank", "Credicorp", "BCP"],
        flag: "🇵🇦",
        flagCode: "pa"
      },
      {
        country: "Colombia", 
        processors: ["Redeban"],
        flag: "🇨🇴",
        flagCode: "co"
      },
      {
        country: "Perú",
        processors: ["Niubiz", "Izipay"],
        flag: "🇵🇪",
        flagCode: "pe"
      }
    ]
  },

  targetAudience: {
    en: [
      {
        icon: Building,
        title: "Banks & Financial Institutions",
        description: "Mid-tier regional banks, digital banks, neobanks, credit unions",
        benefits: [
          "Expand merchant services without building core infrastructure",
          "New fee income streams to compete with global payment facilitators",
          "Embedded payments for digital banking products",
          "Faster time-to-market for payment services"
        ],
        examples: "Ualá, Clara, Regional Banks"
      },
      {
        icon: TrendingUp,
        title: "Payment Facilitators & Aggregators",
        description: "Payment service providers and merchant aggregators",
        benefits: [
          "Modern cloud-native gateway to replace legacy systems",
          "Skip building tech from scratch in emerging markets",
          "Scale merchant onboarding operations efficiently",
          "Compliance and regulatory requirements handled"
        ],
        examples: "Kushki, Allpago, PagSeguro"
      },
      {
        icon: Users,
        title: "Merchant Acquirers & ISOs",
        description: "Independent sales organizations and merchant acquirers",
        benefits: [
          "Own branded solution instead of reselling others",
          "Move from on-premises to modern SaaS systems",
          "Capture higher margins with direct control",
          "Improve merchant retention and stickiness"
        ],
        examples: "Getnet, Cielo, Regional Acquirers"
      },
      {
        icon: Globe,
        title: "Large Enterprises & Retail",
        description: "Airlines, marketplaces, delivery apps, retail chains",
        benefits: [
          "Control payment fees and routing decisions",
          "Multi-acquirer routing for optimization",
          "Advanced tokenization and fraud reduction",
          "Custom payment flows for business models"
        ],
        examples: "Mercado Pago, E-commerce Platforms"
      },
      {
        icon: Zap,
        title: "Fintechs & Startups",
        description: "Lending platforms, wallets, crypto ramps, SuperApps",
        benefits: [
          "In-house payments for better collection rates",
          "Embedded payments in financial products",
          "Compliant card acquiring for crypto services",
          "Speed and flexibility for rapid scaling"
        ],
        examples: "Lending Fintechs, Crypto Platforms"
      },
      {
        icon: Shield,
        title: "Government & Public Sector",
        description: "Tax agencies, municipalities, universities, utilities",
        benefits: [
          "Cost-effective citizen payment solutions",
          "Support for national digitalization initiatives",
          "Transparent and compliant payment processing",
          "Modern infrastructure for public services"
        ],
        examples: "Tax Agencies, Public Universities"
      }
    ],
    es: [
      {
        icon: Building,
        title: "Bancos e Instituciones Financieras",
        description: "Bancos regionales de nivel medio, bancos digitales, neobancos, cooperativas de crédito",
        benefits: [
          "Expandir servicios comerciales sin construir infraestructura central",
          "Nuevas fuentes de ingresos por comisiones para competir con facilitadores de pago globales",
          "Pagos embebidos para productos bancarios digitales",
          "Tiempo de comercialización más rápido para servicios de pago"
        ],
        examples: "Ualá, Clara, Bancos Regionales"
      },
      {
        icon: TrendingUp,
        title: "Facilitadores de Pago y Agregadores",
        description: "Proveedores de servicios de pago y agregadores de comerciantes",
        benefits: [
          "Pasarela moderna nativa en la nube para reemplazar sistemas legacy",
          "Evitar construir tecnología desde cero en mercados emergentes",
          "Escalar operaciones de incorporación de comerciantes eficientemente",
          "Requisitos de cumplimiento y regulatorios manejados"
        ],
        examples: "Kushki, Allpago, PagSeguro"
      },
      {
        icon: Users,
        title: "Adquirentes e ISOs",
        description: "Organizaciones de ventas independientes y adquirentes de comerciantes",
        benefits: [
          "Solución con marca propia en lugar de revender otras",
          "Migrar de sistemas locales a SaaS modernos",
          "Capturar márgenes más altos con control directo",
          "Mejorar retención y adhesión de comerciantes"
        ],
        examples: "Getnet, Cielo, Adquirentes Regionales"
      },
      {
        icon: Globe,
        title: "Grandes Empresas y Retail",
        description: "Aerolíneas, marketplaces, apps de delivery, cadenas retail",
        benefits: [
          "Controlar tarifas de pago y decisiones de enrutamiento",
          "Enrutamiento multi-adquirente para optimización",
          "Tokenización avanzada y reducción de fraudes",
          "Flujos de pago personalizados para modelos de negocio"
        ],
        examples: "Mercado Pago, Plataformas E-commerce"
      },
      {
        icon: Zap,
        title: "Fintechs y Startups",
        description: "Plataformas de préstamos, billeteras, rampas crypto, SuperApps",
        benefits: [
          "Pagos internos para mejores tasas de cobranza",
          "Pagos embebidos en productos financieros",
          "Adquisición de tarjetas compatible para servicios crypto",
          "Velocidad y flexibilidad para escalamiento rápido"
        ],
        examples: "Fintechs de Préstamos, Plataformas Crypto"
      },
      {
        icon: Shield,
        title: "Gobierno y Sector Público",
        description: "Agencias tributarias, municipalidades, universidades, servicios públicos",
        benefits: [
          "Soluciones de pago ciudadano costo-efectivas",
          "Soporte para iniciativas de digitalización nacional",
          "Procesamiento de pagos transparente y compatible",
          "Infraestructura moderna para servicios públicos"
        ],
        examples: "Agencias Tributarias, Universidades Públicas"
      }
    ]
  },

  faqs: {
    en: [
      {
        question: "What is a white-label payment gateway?",
        answer: "A white-label payment gateway is a fully customizable payment processing solution that you can brand as your own. It includes all the infrastructure, APIs, and features needed to process payments, but with your company's branding, domain, and customization."
      },
      {
        question: "How quickly can I get started?",
        answer: "You can get started within 24 hours. Our automated onboarding process includes account setup, basic branding configuration, and API access. Full customization typically takes 1-2 weeks depending on your requirements."
      },
      {
        question: "Do you support international payments?",
        answer: "Yes, we support payments in 150+ countries and currencies, with local payment methods for each region. Our platform handles currency conversion, local regulations, and compliance automatically."
      },
      {
        question: "What about PCI compliance?",
        answer: "We maintain Level 1 PCI DSS compliance, the highest security standard. Your platform inherits this compliance, reducing your regulatory burden and ensuring customer payment data is fully protected."
      },
      {
        question: "Can I integrate with my existing systems?",
        answer: "Absolutely. We provide comprehensive APIs, SDKs for multiple programming languages, and webhooks for real-time notifications. Our technical team can assist with custom integrations as needed."
      },
      {
        question: "What kind of support do you provide?",
        answer: "We offer 24/7 technical support, dedicated account management for enterprise clients, comprehensive documentation, and implementation assistance. Our team has extensive experience in payment processing and fintech."
      }
    ],
    es: [
      {
        question: "¿Qué es una pasarela de pago de marca blanca?",
        answer: "Una pasarela de pago de marca blanca es una solución de procesamiento de pagos completamente personalizable que puedes marcar como tuya. Incluye toda la infraestructura, APIs y características necesarias para procesar pagos, pero con la marca, dominio y personalización de tu empresa."
      },
      {
        question: "¿Qué tan rápido puedo comenzar?",
        answer: "Puedes comenzar en 24 horas. Nuestro proceso automatizado de incorporación incluye configuración de cuenta, configuración básica de marca y acceso a API. La personalización completa típicamente lleva 1-2 semanas dependiendo de tus requerimientos."
      },
      {
        question: "¿Soportan pagos internacionales?",
        answer: "Sí, soportamos pagos en más de 150 países y monedas, con métodos de pago locales para cada región. Nuestra plataforma maneja conversión de monedas, regulaciones locales y cumplimiento automáticamente."
      },
      {
        question: "¿Qué hay sobre el cumplimiento PCI?",
        answer: "Mantenemos el cumplimiento PCI DSS Nivel 1, el estándar de seguridad más alto. Tu plataforma hereda este cumplimiento, reduciendo tu carga regulatoria y asegurando que los datos de pago del cliente estén completamente protegidos."
      },
      {
        question: "¿Puedo integrar con mis sistemas existentes?",
        answer: "Por supuesto. Proporcionamos APIs completas, SDKs para múltiples lenguajes de programación y webhooks para notificaciones en tiempo real. Nuestro equipo técnico puede asistir con integraciones personalizadas según sea necesario."
      },
      {
        question: "¿Qué tipo de soporte proporcionan?",
        answer: "Ofrecemos soporte técnico 24/7, gestión de cuentas dedicada para clientes empresariales, documentación completa y asistencia de implementación. Nuestro equipo tiene amplia experiencia en procesamiento de pagos y fintech."
      }
    ]
  },

  footer: {
    en: {
      product: {
        title: "Product",
        links: [
          { name: "Features", href: "#features" },
          { name: "Services", href: "#services" },
          { name: "Pricing", href: "#pricing" },
          { name: "Integration", href: "#" },
          { name: "API Docs", href: "#" }
        ]
      },
      company: {
        title: "Company", 
        links: [
          { name: "About", href: "#" },
          { name: "Blog", href: "#" },
          { name: "Careers", href: "#" },
          { name: "Contact", href: "#" }
        ]
      },
      support: {
        title: "Support",
        links: [
          { name: "Help Center", href: "#" },
          { name: "Status", href: "#" },
          { name: "Security", href: "#" },
          { name: "Privacy", href: "#" }
        ]
      },
      bottomLinks: [
        { name: "Terms", href: "#" },
        { name: "Privacy", href: "#" },
        { name: "Cookies", href: "#" }
      ]
    },
    es: {
      product: {
        title: "Producto",
        links: [
          { name: "Características", href: "#features" },
          { name: "Servicios", href: "#services" },
          { name: "Precios", href: "#pricing" },
          { name: "Integración", href: "#" },
          { name: "Documentación API", href: "#" }
        ]
      },
      company: {
        title: "Empresa",
        links: [
          { name: "Acerca de", href: "#" },
          { name: "Blog", href: "#" },
          { name: "Carreras", href: "#" },
          { name: "Contacto", href: "#" }
        ]
      },
      support: {
        title: "Soporte",
        links: [
          { name: "Centro de Ayuda", href: "#" },
          { name: "Estado", href: "#" },
          { name: "Seguridad", href: "#" },
          { name: "Privacidad", href: "#" }
        ]
      },
      bottomLinks: [
        { name: "Términos", href: "#" },
        { name: "Privacidad", href: "#" },
        { name: "Cookies", href: "#" }
      ]
    }
  }
};

export default mockData;